import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { copyToClipboard } from '../lib/clipboard';

/**
 * PixCheckout - Painel de pagamento PIX (EMV-QR) - INDEPENDENTE.
 * Recebe valor, txid e callback ao confirmar pagamento.
 */
const PixCheckout = ({ amount, txid, description, onAlreadyPaid }) => {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const generate = async () => {
      setLoading(true);
      setError(null);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backendUrl}/api/pix/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: Number(amount) || 0,
            txid: (txid || '***').toString().substring(0, 25),
            description: description || 'Compra KITS TOP',
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Erro ao gerar PIX');
        }
        const data = await res.json();
        setPayload(data.payload);
        setReceiverName(data.receiver_name);
        setPixKey(data.pix_key);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    generate();
  }, [amount, txid, description]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(payload);
    if (ok) {
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error('Não foi possível copiar. Selecione o texto manualmente.');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center py-10 gap-3" data-testid="pix-loading">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        <p className="text-sm text-slate-600">Gerando QR Code PIX...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700" data-testid="pix-error">
        <p className="font-medium mb-1">Não foi possível gerar o PIX</p>
        <p className="text-xs">{error}</p>
        <p className="text-xs mt-2 text-red-600">
          Verifique se a chave PIX está cadastrada em <strong>Painel Admin → Configurações → Pagamentos</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pix-checkout-panel">
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-emerald-700 font-medium uppercase tracking-wide">Pagamento PIX</p>
            <p className="text-2xl font-bold text-emerald-700 mt-0.5">
              R$ {Number(amount).toFixed(2).replace('.', ',')}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500">Recebedor</p>
            <p className="text-xs font-semibold text-slate-700">{receiverName}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 flex justify-center" data-testid="pix-qr">
          <QRCodeSVG value={payload} size={220} level="M" includeMargin={false} />
        </div>

        <p className="text-xs text-slate-600 text-center mt-3">
          Abra o app do seu banco, escolha <strong>PIX → Ler QR Code</strong> e aponte para a imagem acima.
        </p>
      </div>

      <div>
        <p className="text-xs font-medium text-slate-700 mb-1.5">Ou use o PIX Copia e Cola:</p>
        <div className="flex gap-2">
          <input
            readOnly
            value={payload}
            data-testid="pix-payload-input"
            onClick={(e) => e.target.select()}
            className="flex-1 text-xs px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 font-mono truncate focus:outline-none focus:border-emerald-400"
          />
          <Button
            onClick={handleCopy}
            data-testid="pix-copy-button"
            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            <span className="ml-1.5 hidden sm:inline">{copied ? 'Copiado' : 'Copiar'}</span>
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-slate-700 space-y-1">
        <p><strong>Como funciona:</strong></p>
        <ol className="list-decimal list-inside space-y-0.5 text-slate-600">
          <li>Copie o código ou escaneie o QR Code</li>
          <li>Pague no app do seu banco</li>
          <li>Clique em &ldquo;Já paguei&rdquo; abaixo</li>
          <li>Confirmaremos seu pedido em instantes</li>
        </ol>
      </div>

      {onAlreadyPaid && (
        <Button
          onClick={onAlreadyPaid}
          data-testid="pix-already-paid-button"
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base font-semibold rounded-xl"
        >
          <CheckCircle2 className="w-5 h-5 mr-2" />
          Já paguei
        </Button>
      )}
    </div>
  );
};

export default PixCheckout;
